/**
 * Audit Reports Contracts
 * API contracts for comprehensive audit reporting functionality
 *
 * Report Types:
 * - user_activity: User activity tracking and summary
 * - login_history: Login/logout events and session history
 * - permission_changes: Permission and role modifications
 * - data_access: Data access and export events
 * - config_changes: System configuration modifications
 * - admin_actions: Administrative operations tracking
 * - security_events: Security-related events
 * - compliance: GDPR, SOC2 compliance reports
 */

import type { AdminPaginationMeta } from './tenant.types.js'

// ─────────────────────────────────────────────────────────────
// ENUMS
// ─────────────────────────────────────────────────────────────

export const AuditReportType = {
  USER_ACTIVITY: 'user_activity',
  LOGIN_HISTORY: 'login_history',
  PERMISSION_CHANGES: 'permission_changes',
  DATA_ACCESS: 'data_access',
  CONFIG_CHANGES: 'config_changes',
  ADMIN_ACTIONS: 'admin_actions',
  SECURITY_EVENTS: 'security_events',
  COMPLIANCE: 'compliance',
} as const
export type AuditReportType = (typeof AuditReportType)[keyof typeof AuditReportType]

export const ReportFormat = {
  CSV: 'csv',
  JSON: 'json',
} as const
export type ReportFormat = (typeof ReportFormat)[keyof typeof ReportFormat]

export const ReportRequestStatus = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
  EXPIRED: 'expired',
} as const
export type ReportRequestStatus = (typeof ReportRequestStatus)[keyof typeof ReportRequestStatus]

export const ScheduleType = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  YEARLY: 'yearly',
} as const
export type ScheduleType = (typeof ScheduleType)[keyof typeof ScheduleType]

export const DateRangeTemplate = {
  LAST_7_DAYS: 'last_7_days',
  LAST_30_DAYS: 'last_30_days',
  LAST_90_DAYS: 'last_90_days',
  THIS_MONTH: 'this_month',
  LAST_MONTH: 'last_month',
  THIS_QUARTER: 'this_quarter',
  LAST_QUARTER: 'last_quarter',
  THIS_YEAR: 'this_year',
  LAST_YEAR: 'last_year',
  CUSTOM: 'custom',
} as const
export type DateRangeTemplate = (typeof DateRangeTemplate)[keyof typeof DateRangeTemplate]

// ─────────────────────────────────────────────────────────────
// REQUEST DTOs
// ─────────────────────────────────────────────────────────────

/**
 * Filters for report generation (vary by report type)
 */
export interface ReportFilters {
  // Common filters
  tenantId?: string
  userId?: string
  dateFrom?: string
  dateTo?: string
  search?: string

  // Action-based filters (for admin_actions, permission_changes, etc.)
  action?: string
  resourceType?: string

  // Compliance-specific filters
  complianceType?: 'GDPR' | 'SOC2' | 'ALL'

  // Security event filters
  eventType?: string
  outcome?: 'success' | 'failure' | 'partial'

  // Data export filters
  exportFormat?: string

  // User role filters
  userRole?: string

  // Grouping options
  groupBy?: 'day' | 'week' | 'month' | 'user' | 'action' | 'tenant'
}

/**
 * Request to generate a report
 */
export interface CreateReportRequest {
  reportType: AuditReportType
  format: ReportFormat
  filters: ReportFilters
  dateFrom: string
  dateTo: string
  emailRecipient?: string
}

/**
 * List report requests query parameters
 */
export interface ListReportRequestsQuery {
  page?: number
  limit?: number
  reportType?: AuditReportType
  status?: ReportRequestStatus
  sortBy?: 'createdAt' | 'completedAt' | 'reportType'
  sortOrder?: 'asc' | 'desc'
}

/**
 * Schedule config based on schedule type
 */
export interface ScheduleConfig {
  // For all types: hour (0-23)
  hour?: number

  // For weekly: day of week (0-6, 0=Sunday)
  dayOfWeek?: number

  // For monthly: day of month (1-31)
  dayOfMonth?: number

  // For quarterly: starting month (1-12)
  startMonth?: number

  // For yearly: month (1-12)
  month?: number
}

/**
 * Request to create/update a report schedule
 */
export interface CreateReportScheduleRequest {
  name: string
  description?: string
  reportType: AuditReportType
  format: ReportFormat
  filters: ReportFilters
  dateRangeTemplate: DateRangeTemplate
  dateRangeOffsetDays?: number
  dateRangeDays?: number
  scheduleType: ScheduleType
  scheduleConfig: ScheduleConfig
  emailRecipients: string[]
  emailSubject?: string
  isActive?: boolean
}

/**
 * Update report schedule request
 */
export interface UpdateReportScheduleRequest {
  name?: string
  description?: string
  filters?: ReportFilters
  dateRangeTemplate?: DateRangeTemplate
  dateRangeOffsetDays?: number
  dateRangeDays?: number
  scheduleType?: ScheduleType
  scheduleConfig?: ScheduleConfig
  emailRecipients?: string[]
  emailSubject?: string
  isActive?: boolean
}

/**
 * List report schedules query parameters
 */
export interface ListReportSchedulesQuery {
  page?: number
  limit?: number
  reportType?: AuditReportType
  isActive?: boolean
}

// ─────────────────────────────────────────────────────────────
// RESPONSE DTOs
// ─────────────────────────────────────────────────────────────

/**
 * Report request response
 */
export interface ReportRequestResponse {
  id: string
  reportType: AuditReportType
  format: ReportFormat
  status: ReportRequestStatus
  progress: number
  filters: ReportFilters
  dateFrom: string
  dateTo: string
  fileName: string | null
  fileSizeBytes: number | null
  recordCount: number | null
  errorMessage: string | null
  emailRecipient: string | null
  emailSentAt: string | null
  downloadUrl: string | null
  expiresAt: string
  scheduleId: string | null
  createdAt: string
  startedAt: string | null
  completedAt: string | null
  processingTimeMs: number | null
  requestedBy: {
    id: string
    email: string
    name: string
  }
  estimatedTime?: string
}

/**
 * List of report requests
 */
export interface ReportRequestListResponse {
  items: ReportRequestResponse[]
  pagination: AdminPaginationMeta
}

/**
 * Report schedule response
 */
export interface ReportScheduleResponse {
  id: string
  name: string
  description: string | null
  reportType: AuditReportType
  format: ReportFormat
  filters: ReportFilters
  dateRangeTemplate: DateRangeTemplate
  dateRangeOffsetDays: number
  dateRangeDays: number | null
  scheduleType: ScheduleType
  scheduleConfig: ScheduleConfig
  emailRecipients: string[]
  emailSubject: string | null
  isActive: boolean
  nextRunAt: string | null
  lastRunAt: string | null
  lastRunStatus: string | null
  totalRuns: number
  successfulRuns: number
  failedRuns: number
  createdAt: string
  updatedAt: string
  createdBy: {
    id: string
    email: string
    name: string
  }
}

/**
 * List of report schedules
 */
export interface ReportScheduleListResponse {
  items: ReportScheduleResponse[]
  pagination: AdminPaginationMeta
}

/**
 * Report statistics summary
 */
export interface ReportStatsResponse {
  totalReports: number
  reportsByType: Record<string, number>
  reportsByStatus: Record<string, number>
  activeSchedules: number
  totalSchedules: number
  averageProcessingTimeMs: number
  mostRequestedReportType: string
  recentReports: ReportRequestResponse[]
}

// ─────────────────────────────────────────────────────────────
// REPORT TEMPLATES
// ─────────────────────────────────────────────────────────────

/**
 * Predefined report template configuration
 */
export interface ReportTemplate {
  id: string
  name: string
  description: string
  reportType: AuditReportType
  defaultFormat: ReportFormat
  defaultFilters: ReportFilters
  defaultDateRangeTemplate: DateRangeTemplate
  category: 'security' | 'compliance' | 'operations' | 'audit'
}

/**
 * Available report templates
 */
export const REPORT_TEMPLATES: readonly ReportTemplate[] = [
  {
    id: 'daily_user_activity',
    name: 'Daily User Activity',
    description: 'Summary of user activities over the last 24 hours',
    reportType: AuditReportType.USER_ACTIVITY,
    defaultFormat: ReportFormat.CSV,
    defaultFilters: {},
    defaultDateRangeTemplate: DateRangeTemplate.LAST_7_DAYS,
    category: 'operations',
  },
  {
    id: 'weekly_login_report',
    name: 'Weekly Login History',
    description: 'Login and logout events for the past week',
    reportType: AuditReportType.LOGIN_HISTORY,
    defaultFormat: ReportFormat.CSV,
    defaultFilters: {},
    defaultDateRangeTemplate: DateRangeTemplate.LAST_7_DAYS,
    category: 'security',
  },
  {
    id: 'permission_audits',
    name: 'Permission Change Audit',
    description: 'All permission and role modifications',
    reportType: AuditReportType.PERMISSION_CHANGES,
    defaultFormat: ReportFormat.CSV,
    defaultFilters: {},
    defaultDateRangeTemplate: DateRangeTemplate.LAST_30_DAYS,
    category: 'audit',
  },
  {
    id: 'data_access_log',
    name: 'Data Access Report',
    description: 'Data export and access events',
    reportType: AuditReportType.DATA_ACCESS,
    defaultFormat: ReportFormat.CSV,
    defaultFilters: {},
    defaultDateRangeTemplate: DateRangeTemplate.LAST_30_DAYS,
    category: 'compliance',
  },
  {
    id: 'security_events',
    name: 'Security Events Summary',
    description: 'Security-related events and incidents',
    reportType: AuditReportType.SECURITY_EVENTS,
    defaultFormat: ReportFormat.CSV,
    defaultFilters: {},
    defaultDateRangeTemplate: DateRangeTemplate.LAST_7_DAYS,
    category: 'security',
  },
  {
    id: 'admin_actions_log',
    name: 'Admin Actions Log',
    description: 'All administrative operations performed',
    reportType: AuditReportType.ADMIN_ACTIONS,
    defaultFormat: ReportFormat.CSV,
    defaultFilters: {},
    defaultDateRangeTemplate: DateRangeTemplate.LAST_30_DAYS,
    category: 'audit',
  },
  {
    id: 'config_changes',
    name: 'Configuration Changes',
    description: 'System configuration modifications',
    reportType: AuditReportType.CONFIG_CHANGES,
    defaultFormat: ReportFormat.CSV,
    defaultFilters: {},
    defaultDateRangeTemplate: DateRangeTemplate.LAST_30_DAYS,
    category: 'audit',
  },
  {
    id: 'gdpr_compliance',
    name: 'GDPR Compliance Report',
    description: 'GDPR-related events and data handling',
    reportType: AuditReportType.COMPLIANCE,
    defaultFormat: ReportFormat.CSV,
    defaultFilters: { complianceType: 'GDPR' },
    defaultDateRangeTemplate: DateRangeTemplate.LAST_90_DAYS,
    category: 'compliance',
  },
  {
    id: 'soc2_compliance',
    name: 'SOC2 Compliance Report',
    description: 'SOC2 compliance audit trail',
    reportType: AuditReportType.COMPLIANCE,
    defaultFormat: ReportFormat.CSV,
    defaultFilters: { complianceType: 'SOC2' },
    defaultDateRangeTemplate: DateRangeTemplate.LAST_90_DAYS,
    category: 'compliance',
  },
] as const

// ─────────────────────────────────────────────────────────────
// REPORT GENERATION METADATA
// ─────────────────────────────────────────────────────────────

/**
 * Report generation metadata (included in response headers)
 */
export interface ReportGenerationMetadata {
  reportId: string
  generatedAt: string
  generatedBy: string
  recordCount: number
  dateRange: {
    from: string
    to: string
  }
  filters: ReportFilters
}

// ─────────────────────────────────────────────────────────────
// TYPE NAMES FOR UI DISPLAY
// ─────────────────────────────────────────────────────────────

export const auditReportTypeNames: Record<AuditReportType, string> = {
  user_activity: 'User Activity',
  login_history: 'Login History',
  permission_changes: 'Permission Changes',
  data_access: 'Data Access',
  config_changes: 'Configuration Changes',
  admin_actions: 'Admin Actions',
  security_events: 'Security Events',
  compliance: 'Compliance',
} as const

export const dateRangeTemplateNames: Record<DateRangeTemplate, string> = {
  last_7_days: 'Last 7 Days',
  last_30_days: 'Last 30 Days',
  last_90_days: 'Last 90 Days',
  this_month: 'This Month',
  last_month: 'Last Month',
  this_quarter: 'This Quarter',
  last_quarter: 'Last Quarter',
  this_year: 'This Year',
  last_year: 'Last Year',
  custom: 'Custom Range',
} as const

export const scheduleTypeNames: Record<ScheduleType, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly',
} as const
